import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, Subscription, catchError, forkJoin, of, timer } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { MarketingCampaignApiService } from '../services/marketing-campaign-api.service';
import {
  CampaignExcludedContact,
  CampaignExcludedSegment,
  CampaignList,
  CampaignManualRecipient,
  CampaignMetrics,
  CampaignStatus,
  MarketingCampaign,
  campaignStatusLabel,
} from '../models/marketing-campaign.model';
import { SegmentService } from '../../segments/services/segment-api.service';
import { Segment } from '../../segments/models/segment.model';
import { CampaignDetailTabsComponent, CampaignTab } from '../campaign-detail-tabs/campaign-detail-tabs.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

export type { CampaignTab };

/**
 * Detalle de un correo de marketing EN MODAL (Inc 29).
 *
 * Sustituye a la antigua página ruteada `campaign-detail/`: el estándar del CRM
 * es el modal de detalle sobre la lista (contactos / leads / deals / tickets).
 * La ruta `/marketing-email/:id` se conserva como deep-link — la resuelve
 * `CampaignListComponent`, que monta este modal encima de la lista.
 *
 * Este componente es el CONTENEDOR: carga los datos (getById + getMetrics),
 * hace el polling con circuit-breaker y ejecuta las acciones. El cuerpo de las
 * pestañas vive en `campaign-detail-tabs` (presentacional).
 */
@Component({
  selector: 'app-campaign-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    CampaignDetailTabsComponent,
    HasPermissionDirective,
  ],
  templateUrl: './campaign-detail-modal.component.html',
})
export class CampaignDetailModalComponent implements OnChanges, OnDestroy {
  private readonly api = inject(MarketingCampaignApiService);
  private readonly segmentApi = inject(SegmentService);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly toast = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly authApi = inject(AuthApiService);

  @Input() isOpen = false;
  @Input() campaignId: number | null = null;
  /** Pestaña con la que abrir (deep-link `?tab=`). */
  @Input() initialTab: CampaignTab = 'overview';

  @Output() close = new EventEmitter<void>();
  /** El correo cambió (cancelado / destrabado / reenviado / eliminado) → la lista debe recargar. */
  @Output() changed = new EventEmitter<void>();
  /** El usuario cambió de pestaña — el padre sincroniza `?tab=` si está en deep-link. */
  @Output() tabChange = new EventEmitter<CampaignTab>();

  private readonly destroy$ = new Subject<void>();

  campaign: MarketingCampaign | null = null;
  metrics: CampaignMetrics | null = null;
  lists: CampaignList[] = [];
  segments: Segment[] = [];
  manualRecipients: CampaignManualRecipient[] = [];
  excludedContacts: CampaignExcludedContact[] = [];
  excludedSegments: CampaignExcludedSegment[] = [];

  ownerName: string | null = null;
  loadingOwner = false;

  loading = true;
  isRefreshing = false;
  resending = false;
  markingFailed = false;
  error: string | null = null;

  activeTab: CampaignTab = 'overview';

  private pollSub?: Subscription;
  private pollFailureCount = 0;
  private readonly maxPollFailures = 3;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTab'] && this.isOpen) {
      this.activeTab = this.initialTab;
    }
    if (changes['isOpen'] || changes['campaignId']) {
      if (this.isOpen && this.campaignId) {
        this.activeTab = this.initialTab;
        this.loadAll();
      } else if (!this.isOpen) {
        this.resetState();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pollSub?.unsubscribe();
  }

  private resetState(): void {
    this.pollSub?.unsubscribe();
    this.campaign = null;
    this.metrics = null;
    this.lists = [];
    this.segments = [];
    this.manualRecipients = [];
    this.excludedContacts = [];
    this.excludedSegments = [];
    this.ownerName = null;
    this.error = null;
    this.isRefreshing = false;
    this.resending = false;
    this.markingFailed = false;
    this.activeTab = 'overview';
  }

  onClose(): void {
    this.close.emit();
  }

  onTabChange(tab: CampaignTab): void {
    this.activeTab = tab;
    this.tabChange.emit(tab);
  }

  private loadAll(): void {
    const id = this.campaignId;
    if (!id) return;
    this.loading = true;
    this.error = null;
    forkJoin({
      campaign: this.api.getById(id),
      metrics: this.api.getMetrics(id).pipe(catchError(() => of(null))),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ campaign, metrics }) => {
        this.campaign = campaign;
        this.metrics = metrics;
        this.loading = false;
        this.startPolling();
        this.loadLists();
        this.loadOwner();
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || 'No se pudo cargar el correo.';
      },
    });
  }

  private loadLists(): void {
    const id = this.campaignId;
    if (!id) return;
    forkJoin({
      lists: this.api.listLists(id).pipe(catchError(() => of([] as CampaignList[]))),
      manualRecipients: this.api.listManualRecipients(id).pipe(catchError(() => of([] as CampaignManualRecipient[]))),
      excludedContacts: this.api.listExcludedContacts(id).pipe(catchError(() => of([] as CampaignExcludedContact[]))),
      excludedSegments: this.api.listExcludedSegments(id).pipe(catchError(() => of([] as CampaignExcludedSegment[]))),
    }).pipe(takeUntil(this.destroy$))
      .subscribe(({ lists, manualRecipients, excludedContacts, excludedSegments }) => {
        this.lists = lists || [];
        this.manualRecipients = manualRecipients || [];
        this.excludedContacts = excludedContacts || [];
        this.excludedSegments = excludedSegments || [];
        const needsSegments = this.lists.length > 0 || this.excludedSegments.length > 0;
        if (needsSegments) {
          this.segmentApi.getSegments(0, 500).pipe(catchError(() => of(null))).subscribe(page => {
            this.segments = page?.content || [];
          });
        }
      });
  }

  /** Resuelve el nombre del responsable (`idUserOwner`) para el header del modal. */
  private loadOwner(): void {
    const ownerId = this.campaign?.idUserOwner;
    if (!ownerId) { this.ownerName = null; return; }
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.id === ownerId) {
      this.ownerName = [currentUser.fullname, currentUser.lastname].filter(Boolean).join(' ');
      return;
    }
    this.loadingOwner = true;
    this.authApi.getUserById(ownerId).pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe(user => {
        this.loadingOwner = false;
        this.ownerName = user ? [user.fullname, user.lastname].filter(Boolean).join(' ') : null;
      });
  }

  get totalExcludedByCampaign(): number {
    return (this.metrics?.excludedByCampaign ?? 0)
      + (this.metrics?.excludedBySuppression ?? 0);
  }

  /**
   * Polling de metrics:
   *  - Cada 8s mientras SCHEDULED / SENDING.
   *  - Cada 60s post-SENT (los opens/clicks siguen llegando).
   *  - Detenido si CANCELLED / FAILED / DRAFT.
   *  - Si N requests consecutivas fallan, detiene el poll y avisa.
   */
  private startPolling(): void {
    this.pollSub?.unsubscribe();
    this.isRefreshing = false;
    this.pollFailureCount = 0;
    if (!this.campaign) return;

    const period = (this.campaign.status === 'SCHEDULED' || this.campaign.status === 'SENDING')
      ? 8000
      : (this.campaign.status === 'SENT' ? 60000 : 0);

    if (period === 0) return;

    const id = this.campaign.id;
    const tick$ = timer(period, period).pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.isRefreshing = true;
        return forkJoin({
          campaign: this.api.getById(id).pipe(catchError(() => of(null))),
          metrics: this.api.getMetrics(id).pipe(catchError(() => of(null))),
        });
      }),
    );

    this.pollSub = tick$.subscribe(({ campaign, metrics }) => {
      this.isRefreshing = false;

      if (campaign === null && metrics === null) {
        this.pollFailureCount++;
        if (this.pollFailureCount >= this.maxPollFailures) {
          this.pollSub?.unsubscribe();
          this.toast.error({
            title: 'Sin actualización',
            message: 'No se pudo refrescar el correo. Reintenta manualmente.',
          });
        }
        return;
      }

      this.pollFailureCount = 0;
      if (campaign) this.campaign = campaign;
      if (metrics) this.metrics = metrics;

      if (this.campaign && (this.campaign.status === 'CANCELLED'
        || this.campaign.status === 'FAILED'
        || this.campaign.status === 'DRAFT')) {
        this.pollSub?.unsubscribe();
        return;
      }
      if (this.campaign?.status === 'SENT' && period === 8000) {
        this.startPolling();
      }
    });
  }

  reload(): void {
    this.loadAll();
  }

  async cancelCampaign(): Promise<void> {
    if (!this.campaign) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Cancelar correo',
      message: '¿Cancelar este correo?',
      confirmText: 'Cancelar correo'
    });
    if (!ok) return;
    this.api.cancel(this.campaign.id).subscribe({
      next: (updated) => {
        this.campaign = updated;
        this.pollSub?.unsubscribe();
        this.changed.emit();
      },
      error: (err) => { this.error = err?.error?.message || 'No se pudo cancelar.'; },
    });
  }

  async deleteCampaign(): Promise<void> {
    if (!this.campaign) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar correo',
      message: '¿Eliminar este correo? Esta acción no se puede deshacer.'
    });
    if (!ok) return;
    this.api.delete(this.campaign.id).subscribe({
      next: () => {
        this.toast.deleted('Correo');
        this.changed.emit();
        this.close.emit();
      },
      error: (err) => {
        const msg = err?.error?.message || 'No se pudo eliminar.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  /**
   * Va al form de edición. NO emitimos `close` antes: la navegación destruye la
   * lista (y con ella este modal), y cerrar primero dispararía además la vuelta
   * a `/marketing-email/estado` del deep-link, compitiendo con esta navegación.
   */
  edit(): void {
    if (!this.campaign) return;
    this.router.navigate(['/marketing-email/editar', this.campaign.id]);
  }

  /** Visible cuando el correo quedó atascado enviando (SENDING). Inc 22. */
  get canMarkFailed(): boolean {
    return this.campaign?.status === 'SENDING';
  }

  /** Destraba un correo atascado en SENDING marcándolo como FAILED. */
  async markFailed(): Promise<void> {
    if (!this.campaign || this.markingFailed) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Marcar como fallido',
      message: 'El correo está atascado enviando. Marcarlo como fallido lo destraba para que puedas reintentar. ¿Continuar?',
      confirmText: 'Marcar como fallido',
    });
    if (!ok) return;
    this.markingFailed = true;
    this.error = null;
    this.api.markFailed(this.campaign.id).subscribe({
      next: (updated) => {
        this.markingFailed = false;
        this.campaign = updated;
        this.pollSub?.unsubscribe();
        this.toast.updated('Correo');
        this.changed.emit();
        this.loadAll();
      },
      error: (err) => {
        this.markingFailed = false;
        const msg = err?.error?.message || 'No se pudo marcar el correo como fallido.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  /** Visible solo cuando el correo ya fue enviado (SENT) o falló (FAILED). */
  get canResendFailed(): boolean {
    return this.campaign?.status === 'SENT' || this.campaign?.status === 'FAILED';
  }

  async resendFailed(): Promise<void> {
    if (!this.campaign || this.resending) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Reenviar fallidos',
      message: 'Se reencolarán los destinatarios cuyo envío falló. ¿Continuar?',
      confirmText: 'Reenviar',
    });
    if (!ok) return;

    this.resending = true;
    this.error = null;
    this.api.resendFailed(this.campaign.id).subscribe({
      next: (summary) => {
        this.resending = false;
        if (summary.queued === 0) {
          this.toast.info({
            title: 'Sin fallidos',
            message: 'No hay destinatarios fallidos para reenviar.',
          });
        } else {
          this.toast.success({
            title: 'Reenvío encolado',
            message: `Reencolados ${summary.queued} destinatario${summary.queued === 1 ? '' : 's'}`
              + (summary.failed ? `, ${summary.failed} fallaron` : '') + '.',
          });
        }
        // Refresca correo + métricas (recipients/contadores).
        this.changed.emit();
        this.loadAll();
      },
      error: (err) => {
        this.resending = false;
        const msg = err?.error?.message || 'No se pudieron reenviar los destinatarios fallidos.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  // ===== Helpers de header =====
  /** Iniciales del nombre del correo para el avatar circular del header. */
  get initials(): string {
    const name = (this.campaign?.name || '').trim();
    if (!name) return '?';
    return name.split(/\s+/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }

  get avatarColorClass(): string {
    const name = this.campaign?.name;
    if (!name) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  }

  statusBadgeClass(status: CampaignStatus): string {
    const map: Record<CampaignStatus, string> = {
      DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      SENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      SENT: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
      CANCELLED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
      FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[status] || map.DRAFT;
  }

  statusLabel(status: CampaignStatus): string {
    return campaignStatusLabel(status);
  }

  /**
   * Solo mostramos el badge "Actualizando…" mientras hay envío activo
   * (SCHEDULED o SENDING) Y hay una request del poll en curso.
   * Estados terminales (SENT/CANCELLED/FAILED/DRAFT) no muestran badge.
   */
  get showRefreshingBadge(): boolean {
    if (!this.isRefreshing || !this.campaign) return false;
    return this.campaign.status === 'SCHEDULED' || this.campaign.status === 'SENDING';
  }
}
